// Global set of image URLs confirmed loaded in this session
export const loadedImageUrls = new Set();

// Preload a batch of URLs, marking them in the cache when done
function preloadBatch(urls) {
  urls.forEach(url => {
    if (!url || loadedImageUrls.has(url)) return;
    const img = new Image();
    img.onload = () => loadedImageUrls.add(url);
    img.src = url;
  });
}

/**
 * Preload images in two priority tiers:
 *  - priorityUrls: loaded immediately (category images, first visible items)
 *  - deferredUrls: loaded shortly after, once priority batch is dispatched
 */
export function preloadImages(urls, { priority = false } = {}) {
  const fresh = urls.filter(url => url && !loadedImageUrls.has(url));
  if (fresh.length === 0) return;
  if (priority) {
    preloadBatch(fresh);
  } else {
    // Defer background images so priority ones get bandwidth first
    setTimeout(() => preloadBatch(fresh), 200);
  }
}

/**
 * Collect image URLs from the catalog, split into priority (category covers)
 * and deferred (all item images).
 */
export function collectCatalogImageUrls(categories) {
  const urls = [];
  const walk = (nodes) => {
    (nodes || []).forEach(cat => {
      (cat.images || []).forEach(img => img?.url && urls.push(img.url));
      (cat.items || []).forEach(item => {
        (item.images || []).forEach(img => img?.url && urls.push(img.url));
      });
      walk(cat.children || []);
    });
  };
  walk(categories);
  return urls;
}

/**
 * Returns { categoryUrls, itemUrls } split so callers can preload
 * category covers with higher priority.
 */
/**
 * Display URL for a catalog image — small thumbnail when the cached catalog has
 * one, falling back to the full-resolution original for records synced before
 * thumbnails were stored.
 */
export function imageDisplayUrl(img) {
  if (!img) return null;
  return img.thumbnail_url || img.url || null;
}

export function splitCatalogImageUrls(categories) {
  const categoryUrls = [];
  const itemUrls = [];
  const walk = (nodes) => {
    (nodes || []).forEach(cat => {
      (cat.images || []).forEach(img => img?.url && categoryUrls.push(img.url));
      (cat.items || []).forEach(item => {
        (item.images || []).forEach(img => img?.url && itemUrls.push(img.url));
      });
      walk(cat.children || []);
    });
  };
  walk(categories);
  return { categoryUrls, itemUrls };
}